import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Button, Modal } from "react-bootstrap";
import { useNavigate } from 'react-router-dom';
import { globalErrorHandler } from '../../../utils/GlobalErrorHandler';

const Rooms = () => {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [properties, setProperties] = useState([]);
  const [floors, setFloors] = useState([]);
  const [filteredFloors, setFilteredFloors] = useState([]);
  const [form, setForm] = useState({ property_id: "", floor_id: "", room_no: "" });
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filterPropertyId, setFilterPropertyId] = useState('');
  const [filterFloorId, setFilterFloorId] = useState('');

  const getRooms = () => {
    setIsLoading(true);
    axios.get(`${process.env.REACT_APP_WEB_SERVICES_URL}/admin/get-rooms`, { withCredentials: true })
      .then(res => {
        if (res.data.status) {
          setRooms(res.data.rooms);
        }
        setIsLoading(false);
      })
      .catch(err => globalErrorHandler(err, navigate));
  };

  const getProperties = () => {
    axios.get(`${process.env.REACT_APP_WEB_SERVICES_URL}/admin/get-properties`, { withCredentials: true })
      .then(res => res.data.status && setProperties(res.data.properties))
      .catch(err => globalErrorHandler(err, navigate));
  };

  const getFloors = () => {
    axios.get(`${process.env.REACT_APP_WEB_SERVICES_URL}/admin/get-floors`, { withCredentials: true })
      .then(res => res.data.status && setFloors(res.data.floors))
      .catch(err => globalErrorHandler(err, navigate));
  };

  const openAddModal = () => {
    setIsEdit(false);
    setForm({ property_id: "", floor_id: "", room_no: "" });
    setFilteredFloors([]);
    setShowModal(true);
  };

  const openEditModal = (room) => {
    setIsEdit(true);
    handlePropertyChange(room.property_id);
    setForm({
      property_id: room.property_id,
      floor_id: room.floor_id,
      room_no: room.room_no,
    });
    setCurrentId(room.id);
    setShowModal(true);
  };

  const handlePropertyChange = (propertyId) => {
    const relatedFloors = floors.filter(f => f.property_id == propertyId);
    setFilteredFloors(relatedFloors);
    setForm(prev => ({ ...prev, property_id: propertyId, floor_id: "" }));
  };

  const validateRoomForm = () => {
    const { property_id, floor_id, room_no } = form;

    if (!property_id || !floor_id || !room_no?.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please select a property, floor, and enter the room number.'
      });
      return false;
    }

    return true;
  };

  const saveRoom = () => {
    if (!validateRoomForm()) return;

    axios.post(`${process.env.REACT_APP_WEB_SERVICES_URL}/admin/create-room`, form, { withCredentials: true })
      .then(res => {
        if (res.data.status) {
          Swal.fire("Success", res.data.message, "success");
          setShowModal(false);
          getRooms();
        }
      })
      .catch(err => globalErrorHandler(err, navigate));
  };

  const updateRoom = () => {
    if (!validateRoomForm()) return;

    axios.post(`${process.env.REACT_APP_WEB_SERVICES_URL}/admin/update-room`, { ...form, id: currentId }, { withCredentials: true })
      .then(res => {
        if (res.data.status) {
          Swal.fire("Success", res.data.message, "success");
          setShowModal(false);
          getRooms();
        }
      })
      .catch(err => globalErrorHandler(err, navigate));
  };

  const deleteRoom = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This room will be deleted.',
      icon: 'warning',
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        axios.post(`${process.env.REACT_APP_WEB_SERVICES_URL}/admin/delete-room`, { id }, { withCredentials: true })
          .then(res => {
            if (res.data.status) {
              Swal.fire("Deleted!", res.data.message, "success");
              getRooms();
            }
          })
          .catch(err => globalErrorHandler(err, navigate));
      }
    });
  };

  useEffect(() => {
    getRooms();
    getProperties();
    getFloors();
  }, []);

  return (
    <div className="container-fluid px-4 mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0 flex-grow-1">Rooms</h3>
        <div className="d-flex align-items-center">
          <select
            className="form-select me-2"
            style={{ minWidth: "200px" }}
            value={filterPropertyId}
            onChange={(e) => {
              setFilterPropertyId(e.target.value);
              setFilterFloorId('');
            }}
          >
            <option value="">All Properties</option>
            {properties.map(p => (
              <option key={p.id} value={p.id}>{p.property_name}</option>
            ))}
          </select>

          <select
            className="form-select me-2"
            style={{ minWidth: "200px" }}
            value={filterFloorId}
            onChange={(e) => setFilterFloorId(e.target.value)}
          >
            <option value="">All Floors</option>
            {floors
              .filter(f => !filterPropertyId || f.property_id == filterPropertyId)
              .map(f => (
                <option key={f.id} value={f.id}>{f.floor_name}</option>
              ))}
          </select>
        </div>
        <Button variant="success" onClick={openAddModal}>Add New Room</Button>
      </div>

      <table className="table table-bordered table-hover shadow-sm">
        <thead className="table-success">
          <tr>
            <th>Property</th>
            <th>Floor</th>
            <th>Room No</th>
            <th>No. of Beds</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr><td colSpan="5">Loading...</td></tr>
          ) : (
            rooms
              .filter(room =>
                (!filterPropertyId || room.property_id == filterPropertyId) &&
                (!filterFloorId || room.floor_id == filterFloorId)
              )
              .map(room => (
                <tr key={room.id}>
                  <td>{room.property_name || 'N/A'}</td>
                  <td>{room.floor_name || 'N/A'}</td>
                  <td>{room.room_no}</td>
                  <td>{room.no_of_beds}</td>
                  <td>
                    <Button variant="info" size="sm" className="me-2" onClick={() => openEditModal(room)}>Edit</Button>
                    <Button variant="danger" size="sm" onClick={() => deleteRoom(room.id)}>Delete</Button>
                  </td>
                </tr>
              ))
          )}
        </tbody>
      </table>

      <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? 'Edit Room' : 'Add Room'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Property</label>
            <select
              className="form-select"
              value={form.property_id}
              onChange={(e) => handlePropertyChange(e.target.value)}
            >
              <option value="">Select Property</option>
              {properties.map(p => (
                <option key={p.id} value={p.id}>{p.property_name}</option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Floor</label>
            <select
              className="form-select"
              value={form.floor_id}
              onChange={(e) => setForm({ ...form, floor_id: e.target.value })}
            >
              <option value="">Select Floor</option>
              {filteredFloors.map(f => (
                <option key={f.id} value={f.id}>{f.floor_name}</option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Room No</label>
            <input
              type="text"
              className="form-control"
              value={form.room_no}
              onChange={(e) => setForm({ ...form, room_no: e.target.value })}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={isEdit ? updateRoom : saveRoom}>
            {isEdit ? 'Update' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Rooms;
